import React from 'react';
import { useFormContext, Controller, Path } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Grid,
  MenuItem,
  Paper,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { EmployeeDetailsForm } from './EmployeeDetailsModal';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const arrayFields: Array<keyof EmployeeDetailsForm['template']> = [
  'earnings',
  'extraHours',
  'allowances',
  'deductions',
  'childSupport'
];

const payPeriodMultipliers: Record<string, number> = {
  Weekly: 1,
  Fortnightly: 2,
  Monthly: 52 / 12
  // Add more pay periods as needed based on your EmploymentTab options
};

interface TemplateTabProps {
  onSave: () => void;
  setTabIndex: (index: number) => void;
}

const TemplateTab: React.FC<TemplateTabProps> = ({ onSave, setTabIndex }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    control,
    formState: { errors },
    setValue,
    watch
  } = useFormContext<EmployeeDetailsForm>();
  const template = watch('template');
  const employment = watch('employment');
  const payRates = watch('payRates');
    const basePayRateType = watch('payRates.basePayRateType') || 'hour';

  const getEquivalentHourlyRate = () => {
    const rate = parseFloat(payRates.basePayRate);
    if (isNaN(rate) || rate === 0) return 0; // Return number instead of string

     switch (basePayRateType) {
       case 'hour':
         return rate;
       case 'day':
         return rate / 8; // Assuming 8-hour workday
       case 'year':
         return rate / 2080; // Assuming 40 hours/week * 52 weeks
       default:
         return 0;
     }
   };

   const baseHourlyRate = getEquivalentHourlyRate();
   const leaveLoadingPercentage = Number(employment?.leaveLoading) || 0; // Convert to number and provide fallback

   // Correct calculation: base rate + (base rate * leave loading percentage / 100)
   const equivalentBasePayRate = baseHourlyRate * (1 + leaveLoadingPercentage / 100);

  const addItem = (field: typeof arrayFields[number], defaultItem: any) => {
    const currentArray = (template[field] as any[]) || [];
    return [...currentArray, defaultItem];
  };

  const removeItem = (field: typeof arrayFields[number], index: number) => {
    const currentArray = (template[field] as any[]) || [];
    return currentArray.filter((_: any, i: number) => i !== index);
  };

  // Compute ordinary time earnings dynamically
  const weeklyHours = parseFloat(employment.weeklyWorkHours) || 0;
  const baseRate = parseFloat(payRates.basePayRate) || 0;
  const payPeriod = employment.payPeriod || 'Weekly';
  const multiplier = payPeriodMultipliers[payPeriod] || 1;
  const periodHours = weeklyHours * multiplier;

  let hourlyRate = baseRate;
  // Adjust hourly rate based on basePayRateType (assuming common types; extend as needed)
  if (payRates.basePayRateType === 'day') {
    const hoursPerDay = weeklyHours / 5; // Assume 5-day week
    hourlyRate = baseRate / (hoursPerDay || 1);
  } else if (payRates.basePayRateType === 'week') {
    hourlyRate = baseRate / (weeklyHours || 1);
  } else if (payRates.basePayRateType === 'month') {
    hourlyRate = baseRate / ((weeklyHours * 52) / 12 || 1);
  } else if (payRates.basePayRateType === 'annual' || payRates.basePayRateType === 'year') {
    hourlyRate = baseRate / (weeklyHours * 52 || 1);
  }
  // Default assumes 'hour'

  const periodAmount = periodHours * hourlyRate;

  return (
    <Box
      component="form"
      onSubmit={onSave}
      sx={{ p: { xs: 1, sm: 3 }, maxWidth: 1200, margin: '0 auto' }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Anything entered here will automatically be added to future payruns when they are created.
      </Typography>

      {/* 1. Template Header */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, mb: 4, bgcolor: 'grey.100', borderRadius: 2 }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          Ordinary time earnings
        </Typography>
        <Typography variant="body1">
          {/* {periodHours.toFixed(1)} hours, ${periodAmount.toFixed(0)}{' '} */}
          {periodHours.toFixed(0)} hours, ${equivalentBasePayRate.toFixed(0)}
          <Tooltip title="You can change ordinary earning settings in the Employment tab">
            <Typography
              component="span"
              color="primary"
              sx={{ cursor: 'pointer', ml: 1 }}
              onClick={() => setTabIndex(2)}
            >
              Employment Tab
            </Typography>
          </Tooltip>
        </Typography>
      </Paper>

      {/* 2. Earnings Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 4
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3 }}>
          Earnings
        </Typography>

        {template.earnings?.map((earning: any, index: number) => (
          <Box key={index} sx={{ mb: 3, p: { xs: 1, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.earnings.${index}.type` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={earning.type}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Bonus"
                      select
                      fullWidth
                      size="small"
                      margin="dense"
                      error={!!errors.template?.earnings?.[index]?.type}
                    >
                      <MenuItem value="Exempt from super">Exempt from super</MenuItem>
                      <MenuItem value="Subject to super">Subject to super</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.earnings.${index}.amount` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={earning.amount}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Amount"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      error={!!errors.template?.earnings?.[index]?.amount}
                      helperText={errors.template?.earnings?.[index]?.amount?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} sx={{ textAlign: 'right' }}>
                <IconButton
                  onClick={() => setValue('template.earnings', removeItem('earnings', index))}
                  color="error"
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() =>
            setValue(
              'template.earnings',
              addItem('earnings', { type: 'Bonus', exemptFromSuper: false, amount: 0 })
            )
          }
          size="small"
          sx={{ mt: 1 }}
        >
          Add Earnings
        </Button>
      </Paper>

      {/* 3. Extra Hours Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 4
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3 }}>
          Extra Hours
        </Typography>

        {template.extraHours?.map((hour: any, index: number) => (
          <Box key={index} sx={{ mb: 3, p: { xs: 1, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.extraHours.${index}.option` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={hour.option}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Option"
                      select
                      fullWidth
                      size="small"
                      margin="dense"
                      error={!!errors.template?.extraHours?.[index]?.option}
                      helperText={errors.template?.extraHours?.[index]?.option?.message}
                    >
                      <MenuItem value="Ordinary hours - Non accrued leave">
                        Ordinary hours - Non accrued
                      </MenuItem>
                      <MenuItem value="Ordinary hours - Accrued leave">
                        Ordinary hours - Accrued leave
                      </MenuItem>
                      <MenuItem value="Overtime - Exempt from super">
                        Overtime - Exempt from super
                      </MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.extraHours.${index}.name` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={hour.name}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      fullWidth
                      size="small"
                      margin="dense"
                      error={!!errors.template?.extraHours?.[index]?.name}
                      helperText={errors.template?.extraHours?.[index]?.name?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} sm={6}>
                <Controller
                  name={`template.extraHours.${index}.hours` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={hour.hours}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Hours"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">hr</InputAdornment>
                      }}
                      error={!!errors.template?.extraHours?.[index]?.hours}
                      helperText={errors.template?.extraHours?.[index]?.hours?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={6}>
                <Controller
                  name={`template.extraHours.${index}.rate` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={hour.rate}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rate"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      error={!!errors.template?.extraHours?.[index]?.rate}
                      helperText={errors.template?.extraHours?.[index]?.rate?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} sx={{ textAlign: 'right' }}>
                <IconButton
                  onClick={() => setValue('template.extraHours', removeItem('extraHours', index))}
                  color="error"
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() =>
            setValue(
              'template.extraHours',
              addItem('extraHours', {
                option: 'Ordinary hours - Accrued leave',
                name: '',
                hours: 0,
                rate: 0
              })
            )
          }
          size="small"
          sx={{ mt: 1 }}
        >
          Add Extra Hours
        </Button>
      </Paper>

      {/* 4. Allowances Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 4
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3 }}>
          Allowances
        </Typography>

        {template.allowances?.map((allowance: any, index: number) => (
          <Box key={index} sx={{ mb: 3, p: { xs: 1, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={3}>
                <Controller
                  name={`template.allowances.${index}.unit` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={allowance.unit}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Unit"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">unit</InputAdornment>
                      }}
                      error={!!errors.template?.allowances?.[index]?.unit}
                      helperText={errors.template?.allowances?.[index]?.unit?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Controller
                  name={`template.allowances.${index}.rate` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={allowance.rate}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rate"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      error={!!errors.template?.allowances?.[index]?.rate}
                      helperText={errors.template?.allowances?.[index]?.rate?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.allowances.${index}.option` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={allowance.option}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Option"
                      select
                      fullWidth
                      size="small"
                      margin="dense"
                      error={!!errors.template?.allowances?.[index]?.option}
                      helperText={errors.template?.allowances?.[index]?.option?.message}
                    >
                      <MenuItem value="Subject to tax and super">Subject to tax and super</MenuItem>
                      <MenuItem value="Subject to tax, exempt from super">
                        Subject to tax, exempt from super
                      </MenuItem>
                      <MenuItem value="Exempt from tax and super">
                        Exempt from tax and super
                      </MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.allowances.${index}.type` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={allowance.type}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Type"
                      select
                      fullWidth
                      size="small"
                      margin="dense"
                      error={!!errors.template?.allowances?.[index]?.type}
                    >
                      <MenuItem value="Cents per km">Cents per km</MenuItem>
                      <MenuItem value="Award transport payment">Award transport payment</MenuItem>
                      <MenuItem value="Laundry">Laundry</MenuItem>
                      <MenuItem value="Overtime meal">Overtime meal</MenuItem>
                      <MenuItem value="Qualifications">Qualifications</MenuItem>
                      <MenuItem value="Task">Task</MenuItem>
                      <MenuItem value="Tool">Tool</MenuItem>
                      <MenuItem value="Travel">Travel</MenuItem>
                      <MenuItem value="Other - general">Other - general</MenuItem>
                      <MenuItem value="Other - home office">Other - home office</MenuItem>
                      <MenuItem value="Other - non-deductible">Other - non-deductible</MenuItem>
                      <MenuItem value="Other - transport/fares">Other - transport/fares</MenuItem>
                      <MenuItem value="Other - uniform">Other - uniform</MenuItem>
                      <MenuItem value="Other - private vehicle">Other - private vehicle</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
                <IconButton
                  onClick={() => setValue('template.allowances', removeItem('allowances', index))}
                  color="error"
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() =>
            setValue(
              'template.allowances',
              addItem('allowances', {
                unit: 0,
                rate: 0,
                option: 'Subject to tax and super',
                type: 'Cents per km'
              })
            )
          }
          size="small"
          sx={{ mt: 1 }}
        >
          Add Allowances
        </Button>
      </Paper>

      {/* 5. Deductions Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 4
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3 }}>
          Deductions
        </Typography>

        {template.deductions?.map((deduction: any, index: number) => (
          <Box key={index} sx={{ mb: 3, p: { xs: 1, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.deductions.${index}.option` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={deduction.option}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Option"
                      select
                      fullWidth
                      size="small"
                      margin="dense"
                      error={!!errors.template?.deductions?.[index]?.option}
                      helperText={errors.template?.deductions?.[index]?.option?.message}
                    >
                      <MenuItem value="Pre-tax deduction">Pre-tax deduction</MenuItem>
                      <MenuItem value="Post-tax deduction">Post-tax deduction</MenuItem>
                      <MenuItem value="Fringe benefit">Fringe benefit</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.deductions.${index}.type` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={deduction.type}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Type"
                      select
                      fullWidth
                      size="small"
                      margin="dense"
                      error={!!errors.template?.deductions?.[index]?.type}
                    >
                      <MenuItem value="Fee">Fee</MenuItem>
                      <MenuItem value="Fee Workplace giving">Fee Workplace giving</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={8} sm={6}>
                <Controller
                  name={`template.deductions.${index}.rate` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={deduction.rate}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rate"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      error={!!errors.template?.deductions?.[index]?.rate}
                      helperText={errors.template?.deductions?.[index]?.rate?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4} sm={6} sx={{ textAlign: 'right' }}>
                <IconButton
                  onClick={() => setValue('template.deductions', removeItem('deductions', index))}
                  color="error"
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() =>
            setValue(
              'template.deductions',
              addItem('deductions', { option: 'Pre-tax deduction', type: 'Fee', rate: 0 })
            )
          }
          size="small"
          sx={{ mt: 1 }}
        >
          Add Deduction
        </Button>
      </Paper>

      {/* 6. Child Support Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 4
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3 }}>
          Child Support
        </Typography>

        {template.childSupport?.map((support: any, index: number) => (
          <Box key={index} sx={{ mb: 3, p: { xs: 1, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.childSupport.${index}.type` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={support.type}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Type"
                      select
                      fullWidth
                      size="small"
                      margin="dense"
                      error={!!errors.template?.childSupport?.[index]?.type}
                    >
                      <MenuItem value="Child support garnishees">Child support garnishees</MenuItem>
                      <MenuItem value="Child support deductions">Child support deductions</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`template.childSupport.${index}.rate` as Path<EmployeeDetailsForm>}
                  control={control}
                  defaultValue={support.rate}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rate"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      error={!!errors.template?.childSupport?.[index]?.rate}
                      helperText={errors.template?.childSupport?.[index]?.rate?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} sx={{ textAlign: 'right' }}>
                <IconButton
                  onClick={() =>
                    setValue('template.childSupport', removeItem('childSupport', index))
                  }
                  color="error"
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() =>
            setValue(
              'template.childSupport',
              addItem('childSupport', { type: 'Child support garnishees', rate: 0 })
            )
          }
          size="small"
          sx={{ mt: 1 }}
        >
          Add Child Support
        </Button>
      </Paper>

      {/* Bottom Sections */}
      <Grid container spacing={isMobile ? 2 : 4}>
        <Grid item xs={12} md={6}>
          {/* 7. Superannuation Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mb: 4
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Superannuation
            </Typography>
            <Tooltip title="You can change super contribution rate in the Bank & Super tab">
              <Typography variant="body2" color="primary" sx={{ mb: 2, cursor: 'pointer' }}>
                Bank & Super tab
              </Typography>
            </Tooltip>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="template.superannuation.employerContribution"
                  control={control}
                  defaultValue={template.superannuation?.employerContribution}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Employer contribution"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      error={!!errors.template?.superannuation?.employerContribution}
                      helperText={errors.template?.superannuation?.employerContribution?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="template.superannuation.superSalarySacrifice"
                  control={control}
                  defaultValue={template.superannuation?.superSalarySacrifice}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Super salary sacrifice"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      error={!!errors.template?.superannuation?.superSalarySacrifice}
                      helperText={errors.template?.superannuation?.superSalarySacrifice?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="template.superannuation.isPercentage"
                  control={control}
                  defaultValue={template.superannuation?.isPercentage}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={!!field.value} size="small" />}
                      label="Switch to %"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          {/* 8. Fixed Tax Amount Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Fixed Tax Amount
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              The fixed tax amount will apply to the payslip when they are created or updated.
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Controller
                  name="template.fixedTaxAmount.enabled"
                  control={control}
                  defaultValue={template.fixedTaxAmount?.enabled}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={!!field.value} size="small" />}
                      label="Enabled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="template.fixedTaxAmount.amount"
                  control={control}
                  defaultValue={template.fixedTaxAmount?.amount}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Fixed tax amount"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      error={!!errors.template?.fixedTaxAmount?.amount}
                      helperText={errors.template?.fixedTaxAmount?.amount?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemplateTab;
